import {FormInstance} from 'antd/es/form';
import {debounce} from 'lodash-es';
import React, {FC, useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';

import {TEditableColumn} from 'components/editable-table';
import {IValidationController} from 'components/editable-table/model/types';
import {IFieldMeta, loadMetadata, selectMetadata} from 'entities/metadata';
import {selectContextRawData} from 'modules/context/context-selector';
import {TEntity, resetLoadedData} from 'modules/data';
import {TLookupValue} from 'modules/data/data-types';
import {convertParamsForRequest} from 'modules/data/utils/data.utils';
import {EEntityType} from 'shared/constants/entities';
import {useEntityFromLocation} from 'shared/hooks/use-entity-from-location';
import {getPrefixedSection, showMessage} from 'shared/utils';
import {useAppSelector} from 'store/config/hooks';
import {
    clearAllSignsInfo,
    clearDocumentsForSign,
    IDocumentUploadSignature,
    IEntitySignatureUploadState,
    fetchDocumentInfo,
    generateSignatureInfoFromDocumentInfo,
    loadDefaultSignInfo,
    loadDocumentsForSign,
    NO_FILES_MESSAGE,
    NOT_FILLED_FIELDS_MESSAGE,
    selectDocumentExtensions,
    selectDocumentList,
    selectDocumentSignatureUpload,
    setSignList,
    SIGN_UNIQUE_FIELD_NAME,
    TOTAL_ENTITY,
    uploadSignatureFiles,
} from 'store/slices/documents-uploading-slice';
import {TModalComponentInterface, selectModalData} from 'store/slices/modals-slice';
import {selectRegions} from 'store/slices/regions-slice';

import {FileUploadModal} from '../components/file-upload-modal';
import {FormsFileModalContext} from '../file-modal/file-modal.context';
import {generateSignColumnsMetadata} from './documents-signature-upload-modal-utils';

const getUniqueFieldValue = (file: IDocumentUploadSignature) => file[SIGN_UNIQUE_FIELD_NAME];

export const DocumentsSignatureUploadModalContainer:
    FC<TModalComponentInterface> = ({
        onClose,
    }: TModalComponentInterface) => {
    const dispatch = useDispatch();
    const regions = useAppSelector(selectRegions);
    const signatureUpload = useAppSelector(selectDocumentSignatureUpload);
    const documentList = useAppSelector(selectDocumentList);
    const extensions = useAppSelector(selectDocumentExtensions);
    const parentEntityName = useAppSelector(selectModalData)?.entityName;
    const [validation, setValidation] = useState<IValidationController>();
    const [form, setForm] = useState<FormInstance | undefined>();
    const [columnsMeta, setColumnsMeta] = useState<TEditableColumn[] | undefined>();
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const {entityName: entityNameFromLocation} = useEntityFromLocation();
    const entityName = entityNameFromLocation ?? TOTAL_ENTITY;
    const signatureUploadSlice = signatureUpload?.[entityName];
    const {signList = []} = signatureUploadSlice ?? {};
    const signListRef = useRef(signList);
    const {validateAllRows} = validation || {};

    useEffect(() => {
        signListRef.current = signList;
    }, [signList]);

    const contextData = useAppSelector(selectContextRawData);

    // использовать modalEntityName в action чтобы сделать тут другие метаданные
    const modalEntityName = parentEntityName === entityNameFromLocation || !parentEntityName
        ? 'document.signature.upload' : parentEntityName;

    const meta = useAppSelector(
        state => selectMetadata(modalEntityName, EEntityType.EDITABLE_TABLE)(state),
    );

    const handleDelete = useCallback((tableRow: TEntity) => {
        const signEntry = tableRow as IDocumentUploadSignature;

        const currentSignList = signListRef.current;

        const resultFileList = currentSignList.filter((sign: IDocumentUploadSignature) => (
            getUniqueFieldValue(sign) !== getUniqueFieldValue(signEntry)
        ));

        dispatch(setSignList({entityName, signList: resultFileList}));
    }, [entityName]);

    useEffect(() => {
        dispatch(loadDocumentsForSign({entityName: entityNameFromLocation}));
        if (!meta) {
            dispatch(loadMetadata(modalEntityName, EEntityType.EDITABLE_TABLE));
        }
    }, []);

    useEffect(() => {
        if (regions && meta?.fields) {
            const columns = (meta.fields as IFieldMeta[]).map(columnMeta => generateSignColumnsMetadata(
                columnMeta,
                regions,
                {
                    documentList: documentList as TLookupValue[] || [],
                    handleDelete,
                    progress: {
                        signUploadSlice: signatureUploadSlice as IEntitySignatureUploadState,
                        fieldKey: SIGN_UNIQUE_FIELD_NAME,
                    },
                },
            ));
            setColumnsMeta(columns);
        }
    }, [signatureUpload, documentList]);

    const handleUpload = debounce((_: File, newSignFiles: File[]) => {
        const emptyFileNames: string[] = [];
        const newFiles: File[] = [];

        newSignFiles.forEach(file => {
            if (!file || file.size === 0) {
                emptyFileNames.push(file?.name);
            } else {
                newFiles.push(file);
            }
        });

        if (emptyFileNames.length > 0) {
            showMessage({message: `Следующие файлы пустые:\n${emptyFileNames.join('\n')}`, isError: true});
        }

        const resolveFiles = newFiles
            .filter(newSign => !signListRef.current.some(oldSign => oldSign.fileName === newSign.name));

        if (!resolveFiles.length) return;

        if (errorMsg === NO_FILES_MESSAGE) {
            setErrorMsg(null);
        }

        dispatch(loadDefaultSignInfo({entityName: entityNameFromLocation, files: resolveFiles}));
    }, 500);

    const handleEdit = async (tableRow: TEntity) => {
        const signatureInfo = tableRow as IDocumentUploadSignature;
        const signatureIndex = signList.findIndex(sign => sign.fileName === signatureInfo.fileName);

        if (signatureIndex === -1) return; // Если индекс не найден — выходим сразу

        const updatedSignList = [...signList]; // Создаём новый массив

        if (signatureInfo.documentFileId === signList[signatureIndex].documentFileId) {
            // Если ID совпадают — просто обновляем
            updatedSignList[signatureIndex] = {
                ...signList[signatureIndex],
                ...signatureInfo,
            };
        } else {
            // Если ID разные — получаем дополнительную информацию
            const {data: chosenDocument} = await fetchDocumentInfo({
                attachId: signatureInfo.documentFileId,
                data: {
                    ...convertParamsForRequest(contextData || {}),
                    entityName: getPrefixedSection(entityName),
                    sgnFileName: signatureInfo.fileName,
                },
            });

            if (chosenDocument) {
                updatedSignList[signatureIndex] = {
                    ...signatureInfo,
                    ...generateSignatureInfoFromDocumentInfo(chosenDocument),
                };
            }
        }

        dispatch(setSignList({entityName, signList: updatedSignList}));
    };

    const handleClear = () => {
        dispatch(clearAllSignsInfo({entityName}));
    };

    const uploadSignatureFileList = () => dispatch(uploadSignatureFiles({files: signList, entityName}));

    const handleClose = () => {
        onClose();
        dispatch(clearDocumentsForSign());
    };

    const onSubmit = async () => {
        if (!signList.length) {
            setErrorMsg(NO_FILES_MESSAGE);
            return;
        }
        try {
            const isValid = validateAllRows ? await validateAllRows(signList) : false;
            if (!isValid) throw new Error('Ошибка при попытке валидации строк таблицы');

            setErrorMsg(null);
            await uploadSignatureFileList();

            if (parentEntityName) {
                dispatch(resetLoadedData(parentEntityName, EEntityType.TABLE));
            }

            handleClear();
            handleClose();
        } catch (e) {
            setErrorMsg(NOT_FILLED_FIELDS_MESSAGE);
            console.error(e);
        }
    };

    return (
        <FormsFileModalContext.Provider
            value={{
                form,
                setForm,
                validation,
                setValidation,
            }}
        >
            <FileUploadModal
                onClose={handleClose}
                onClear={handleClear}
                onSubmit={onSubmit}
                handleEdit={handleEdit}
                onUpload={handleUpload}
                columns={columnsMeta}
                columnsFiltered
                accept={extensions?.accept || ''}
                fileList={signList}
                errorMsg={errorMsg}
                modalTitle={meta?.title}
                isPageJumperEnabled={meta?.isPageJumperEnabled}
                isPageSizeChangerEnabled={meta?.isPageJumperEnabled}
                rowKey="fileName"
                submitText="Загрузить добавленные ЭП"
            />
        </FormsFileModalContext.Provider>
    );
};
